<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';
import {
  useMapGetter,
} from 'dashboard/composables/store.js';
const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  inboxId: {
    type: Number,
    default: 0,
  }
});

const inboxGetter = useMapGetter('inboxes/getInboxById');

const inbox = computed(() => {
  return inboxGetter.value(props.inboxId);
});

// 判断是否显示 Private Message 选项
const showPrivateMessage = computed(() => {
  return inbox.value.ubShowPrivateMessage;
});

// 返回的reply mode type
const privateMessageReplyMode = computed(() => {
  return inbox.value.ubPrivateMessageReplyMode?.toUpperCase() || '';
})

defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');
const wootEditorPrivateMessageMode = useTemplateRef('wootEditorPrivateMessageMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);
const privateMessageModeSize = useElementSize(wootEditorPrivateMessageMode);

/**
 * Computed boolean indicating if the editor is in private note mode
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => props.mode === REPLY_EDITOR_MODES.NOTE);

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  if (props.mode === REPLY_EDITOR_MODES.NOTE) {
    return `${privateModeSize.width.value + 16}px`;
  }
  if (props.mode === REPLY_EDITOR_MODES.REPLY) {
    return `${replyModeSize.width.value + 16}px`;
  }
  if (inbox.value.ubPrivateMessageReplyMode?.toUpperCase() === props.mode) {
    return `${privateMessageModeSize.width.value + 16}px`;
  }
  return `${replyModeSize.width.value + 16}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  if (inbox.value.ubPrivateMessageReplyMode?.toUpperCase() === props.mode) {
    return `${replyModeSize.width.value + privateModeSize.width.value + 32}px`;
  }
  if (props.mode === REPLY_EDITOR_MODES.NOTE) {
    return `${replyModeSize.width.value + 16}px`;
  }
  if (props.mode === REPLY_EDITOR_MODES.REPLY) {
    return 0;
  }
  return 0
});
</script>

<template>
  <button
    class="flex items-center w-auto h-8 p-1 transition-all border rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0"
  >
    <div ref="wootEditorReplyMode" class="flex items-center gap-1 px-2 z-20" @click="$emit('toggleMode', REPLY_EDITOR_MODES.REPLY)">
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </div>
    <div ref="wootEditorPrivateMode" class="flex items-center gap-1 px-2 z-20" @click="$emit('toggleMode', REPLY_EDITOR_MODES.NOTE)">
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </div>
    <div 
      v-if="showPrivateMessage"
      ref="wootEditorPrivateMessageMode" 
      class="flex items-center gap-1 px-2 z-20" 
      @click="$emit('toggleMode', privateMessageReplyMode)"
    >
      {{ "Reply via Private Message" }}
    </div>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] transition-all duration-300 ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
